import { useState } from "react";
import { Link2, AlertTriangle, Sparkles, Loader2, ChevronDown, ChevronRight, Image as ImageIcon, FileStack, FileText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface HazardProvenanceItem {
  label: string;
  detail?: string;
  sources: Array<{ kind: "photo" | "plan" | "doc"; fileName: string }>;
}

export interface CorrelationSummaryRowProps {
  status: "idle" | "running" | "completed" | "skipped" | "error";
  evidenceLinks?: number;
  newHazards?: number;
  contradictions?: number;
  /** When provided, renders an expandable provenance list showing which attachments produced which new hazards. */
  hazardProvenance?: HazardProvenanceItem[];
}

const KIND_ICON = {
  photo: ImageIcon,
  plan: FileStack,
  doc: FileText,
} as const;

export function CorrelationSummaryRow({
  status,
  evidenceLinks = 0,
  newHazards = 0,
  contradictions = 0,
  hazardProvenance,
}: CorrelationSummaryRowProps) {
  const [expanded, setExpanded] = useState(false);
  if (status === "idle" || status === "skipped") return null;
  const hasProvenance = !!hazardProvenance && hazardProvenance.length > 0;

  return (
    <div className="mb-6 min-w-0">
      <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Cross-Reference
      </h4>

      {status === "running" && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin flex-shrink-0" />
          <span className="truncate">Cross-referencing insights against attachments...</span>
        </div>
      )}

      {status === "error" && (
        <div className="text-xs text-destructive">Cross-referencing failed — continuing without correlations.</div>
      )}

      {status === "completed" && (
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Link2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span className="truncate">
              <strong className="text-foreground tabular-nums">{evidenceLinks}</strong> evidence {evidenceLinks === 1 ? "link" : "links"} found
            </span>
          </div>
          {newHazards > 0 && (
            <div className="min-w-0">
              {hasProvenance ? (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="flex w-full items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {expanded ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />}
                  <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <span className="truncate text-left">
                    <strong className="text-foreground tabular-nums">{newHazards}</strong> new {newHazards === 1 ? "hazard" : "hazards"} from attachments
                  </span>
                </button>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <span className="truncate">
                    <strong className="text-foreground tabular-nums">{newHazards}</strong> new {newHazards === 1 ? "hazard" : "hazards"} from attachments
                  </span>
                </div>
              )}
              {hasProvenance && expanded && (
                <TooltipProvider delayDuration={150}>
                  <ul className="mt-2 ml-5 space-y-1.5">
                    {hazardProvenance!.map((h, idx) => (
                      <li key={idx} className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 text-foreground cursor-help">
                              <Sparkles className="h-3 w-3 text-primary flex-shrink-0" />
                              <span className="truncate">{h.label}</span>
                            </span>
                          </TooltipTrigger>
                          {h.detail && (
                            <TooltipContent side="top" className="max-w-xs text-xs">
                              {h.detail}
                            </TooltipContent>
                          )}
                        </Tooltip>
                        <span className="text-muted-foreground/70 italic">from</span>
                        {h.sources.map((s, sIdx) => {
                          const Icon = KIND_ICON[s.kind];
                          return (
                            <span
                              key={sIdx}
                              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground max-w-[180px]"
                            >
                              <Icon className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{s.fileName}</span>
                            </span>
                          );
                        })}
                        {h.sources.length === 0 && (
                          <span className="text-[11px] text-muted-foreground/60">unknown source</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </TooltipProvider>
              )}
            </div>
          )}
          {contradictions > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
              <span className="truncate">
                <strong className="text-foreground tabular-nums">{contradictions}</strong> {contradictions === 1 ? "contradiction" : "contradictions"} flagged
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
